// Self-service timesheets ("My timesheets"). Read-only list of the
// authenticated user's own pay-period timesheets (self.read_timesheets).

import type { Timesheet, TimesheetStatus } from '@dsr/shared-types';
import { useMyTimesheets } from '../api/queries/self';
import { ApiError } from '../api/client';
import { fmtDate } from '../utils/date';
import { Badge, Table, type BadgeTone } from '../components/ui';
import './reports.css';

const STATUS_LABEL: Record<TimesheetStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  exported: 'Exported',
};

// Status → shared Badge tone (matches the old rpt-badge palette used by the
// staff Timesheets page: submitted=info, approved=success, exported=warn).
const STATUS_TONE: Record<TimesheetStatus, BadgeTone> = {
  draft: 'neutral',
  submitted: 'info',
  approved: 'success',
  exported: 'warn',
};

function period(ts: Timesheet): string {
  if (!ts.period_start && !ts.period_end) return '—';
  return `${fmtDate(ts.period_start)} – ${fmtDate(ts.period_end)}`;
}

export function MyTimesheetsPage(): JSX.Element {
  const { data, isPending, isError, error } = useMyTimesheets();

  if (isPending) {
    return <p className="rpt-status-line">Loading your timesheets…</p>;
  }
  if (isError || !data) {
    return (
      <p className="rpt-status-line rpt-status-error" role="alert">
        {error instanceof ApiError ? error.message : 'Failed to load your timesheets.'}
      </p>
    );
  }

  const timesheets = [...data.timesheets].sort(
    (a, b) => (b.period_start ?? b.created_at) - (a.period_start ?? a.created_at),
  );

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>My timesheets</h1>
          <p className="page-sub">Your pay-period hours and their approval status.</p>
        </div>
      </header>

      {timesheets.length === 0 ? (
        <p className="rpt-empty">You have no timesheets on file.</p>
      ) : (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Pay period</Table.HeaderCell>
              <Table.HeaderCell>Regular</Table.HeaderCell>
              <Table.HeaderCell>OT</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {timesheets.map((ts) => (
              <Table.Row key={ts.id}>
                <Table.Cell>{period(ts)}</Table.Cell>
                <Table.Cell tone="mono">{ts.regular_hours}</Table.Cell>
                <Table.Cell tone="mono">{ts.ot_hours}</Table.Cell>
                <Table.Cell>
                  <Badge tone={STATUS_TONE[ts.status]}>{STATUS_LABEL[ts.status]}</Badge>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </section>
  );
}
